import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Prompt} from 'react-router';
import {useHistory} from 'react-router-dom';
import {Tabs, TabList, TabPanel, TabPanels, Tab} from '@reach/tabs';
import {push} from 'connected-react-router/immutable';
import {Trans} from '@lingui/react';
import {Helmet} from 'react-helmet';
import {i18n} from '@services/i18n';
import PropTypes from 'prop-types';

import {HOME, EVALUATION360} from '@services/routes';
import {getUserCampaign} from '@reducers/userCampaign';
import {stopTestingItem} from '@actions/tests';
import {getCurrentTest} from '@reducers/tests';
import {resetEvaluation} from '@actions/evaluationQuestions';
import {loadQuestionnaire360, updateSkillup360Status} from '@actions/questionnaire360';
import {getTests360, getLoadingState} from '@reducers/questionnaire360';
import {getTestExitMessage, getEvaluationStartMessage} from '@services/alerts';
import useFetch from '@hooks/useFetch';

import Details360 from '@components/Skillup360/Details360';
import EvaluationList from '@components/Skillup360/EvaluationList';
import LoadingSpinner from '@components/LoadingSpinner';

import cls from 'classnames';
import s from './Dashboard360.css';

function TabBar(props) {
  const {selected} = props;

  return (
    <>
      <TabList className={s.Dashboard360TabBar} aria-describedby="tab-navigation-hint">
        <Tab className={cls(s.Tab, selected === 0 ? s.Selected : '')}>
          <span>
            <Trans id="dashboard360.tab.show_all">Mostra tutti</Trans>
          </span>
        </Tab>

        <Tab className={cls(s.Tab, selected === 1 ? s.Selected : '')}>
          <span>
            <Trans id="dashboard360.tab.exclude_rated_names">Escludi nomi già valutati</Trans>
          </span>
        </Tab>
      </TabList>
      <span id="tab-navigation-hint" className="visually-hidden">
        <Trans id="dashboard360.tab.navigation_hint">
          Usa le frecce sinistra e destra per navigare tra i tab
        </Trans>
      </span>
    </>
  );
}

TabBar.propTypes = {
  selected: PropTypes.string,
  onClick: PropTypes.func,
};

export default function Dashboard360() {
  const currentCampaign = useSelector(getUserCampaign);
  const currentCampaignId = currentCampaign.get('id');
  const currentTest = useSelector(getCurrentTest);
  const currentTestId = currentTest.get('id');
  const tests360 = useSelector(getTests360);
  const isLoadingQ360 = useSelector(getLoadingState);
  const toEvaluateUsers = useSelector(state => state.getIn(['questionnaire360', 'data']));
  const loggedUserId = useSelector(state => state.getIn(['user', 'data', 'id']));
  const evaluationLoadingState = useSelector(state =>
    state.getIn(['evaluationQuestions', 'evaluationLoadingState'])
  );

  const history = useHistory();
  const dispatch = useDispatch();
  const {data: storeData} = useFetch(
    `tests/${currentTestId}/status/${currentCampaignId}/${loggedUserId}`
  );
  const version = storeData?.data.version;

  const [selectedTab, setSelectedTab] = useState(0);
  const [promptEnabled, setPromptEnabled] = useState(false);

  let start_message = '';

  useEffect(() => {
    //coming back from questinnaire360
    //make sure to reset specific user data
    if (evaluationLoadingState === 'SUCCESS') {
      dispatch(resetEvaluation());
    }
  }, [evaluationLoadingState]);

  useEffect(() => {
    if (currentTest.get('id') != 360 || !currentCampaign) {
      returnToBase();
      return;
    }

    dispatch(loadQuestionnaire360(currentCampaignId));
    setPromptEnabled(true);

    if (currentTest.get('status') === 'not attempted') {
      dispatch(updateSkillup360Status(currentCampaignId, currentTestId, 'incomplete', 1));
    }

    return () => {
      setPromptEnabled(false);

      if (history.location.pathname === HOME) {
        dispatch(stopTestingItem());
      }
    };
  }, []);

  useEffect(() => {
    if (currentTest.get('status') === 'complete' || !currentCampaignId || !currentTestId) {
      return;
    }

    const usersToEvaluateIsEmpty = toEvaluateUsers?.size < 1 || toEvaluateUsers?.length < 1;

    const allUsersEvaluated =
      !usersToEvaluateIsEmpty &&
      toEvaluateUsers.every(user => user.get('user_is_evaluated') === 'y');

    if (allUsersEvaluated) {
      if (version) {
        dispatch(updateSkillup360Status(currentCampaignId, currentTestId, 'complete', version + 1));
      }
    }
  }, [toEvaluateUsers, currentCampaignId, currentTestId, version]);

  function returnToBase(where = HOME) {
    dispatch(push(where));
  }

  function handleEnterTest(evaluee) {
    const selected = tests360?.find(t => t.get('id') === evaluee);

    if (selected) {
      start_message = `${i18n._('dashboard360.start_message', null, {
        defaults: `Stai per valutare ${selected.get('firstName')} ${selected.get('lastName')}`,
      })} ${selected.get('firstName')} ${selected.get('lastName')}`;
    }

    const isSelfEval = selected?.get('relationId') === 3;

    dispatch(push({pathname: EVALUATION360, state: {evaluee, isSelfEval}}));
  }

  function handleChangeTab(index) {
    setSelectedTab(index);
  }

  const filteredTests =
    selectedTab === 1
      ? tests360.filter(test => !(test.get('user_is_evaluated') === 'y'))
      : tests360;
  return (
    <>
      <Helmet>
        <title>
          {i18n._('dashboard360.page_title', null, {
            defaults: 'Skillup Questionario 360 - Dashboard',
          })}
        </title>
      </Helmet>

      <Prompt
        when={promptEnabled}
        message={next_loc => {
          let to_return = true;

          if (next_loc.pathname === HOME) {
            to_return = getTestExitMessage(
              null,
              i18n._('dashboard360.home_redirect_message', null, {
                defaults: 'Sarai reindirizzato a Home',
              })
            );
          }

          if (next_loc.pathname === EVALUATION360) {
            to_return = getEvaluationStartMessage(start_message);
          }

          return to_return;
        }}
      />

      <div className={s.Dashboard360}>
        <h1 className="visually-hidden" aria-live="assertive" aria-atomic="true">
          {i18n._('dashboard360.page_title', null, {
            defaults: 'Skillup Questionario 360 - Dashboard',
          })}
        </h1>
        {isLoadingQ360 === 'PENDING' && <LoadingSpinner />}

        {isLoadingQ360 === 'SUCCESS' && (
          <Tabs index={selectedTab} onChange={handleChangeTab}>
            <TabBar selected={selectedTab} />

            <TabPanels>
              <TabPanel>
                <Dashboard
                  currentCampaign={currentCampaign}
                  tests360={tests360}
                  filteredTests={filteredTests}
                  handleEnterTest={handleEnterTest}
                />
              </TabPanel>

              <TabPanel>
                <Dashboard
                  currentCampaign={currentCampaign}
                  tests360={tests360}
                  filteredTests={filteredTests}
                  handleEnterTest={handleEnterTest}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}
      </div>
    </>
  );
}

function Dashboard({filteredTests, currentCampaign, tests360, handleEnterTest}) {
  return (
    <div className={s.Dashboard360Container}>
      <div className={s.Dashboard360DetailsContainer}>
        <Details360
          evaluations={tests360}
          campaignStart={currentCampaign.get('dateStart')}
          campaignEnd={currentCampaign.get('dateEnd')}
        />
      </div>

      <div className={s.Dashboard360TestContainer}>
        <EvaluationList
          campaign={currentCampaign}
          tests={filteredTests}
          onEnterTest={handleEnterTest}
        />
      </div>
    </div>
  );
}
